import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useDemo } from '../../contexts/DemoContext';
import { getTeamMembersRecursive, TeamMemberWithLevel, updateTeamMemberTitle, getAllManagers, toggleCanImpersonate } from '../../services/userService';
import {
  getTeamGoalsRecursive,
  getCurrentQuarter,
  toggleAllowEdit,
  setSelfReviewOpenDate,
  isSelfReviewOpenByDate,
  getGlobalSelfReviewSettings,
  setGlobalSelfReviewDate
} from '../../services/goalsService';
import { demoTeamAssignments, demoGoals } from '../../services/demoData';
import { GoalDocument, RoleDocument } from '../../types';
import { 
  Users, 
  CheckCircle2, 
  History,
  ChevronDown,
  ChevronUp,
  Search,
  AlertCircle,
  Download,
  Maximize2,
  Minimize2,
  ChevronRight,
  Filter,
  Pencil,
  X,
  Save,
  Eye,
  PenLine,
  Calendar,
  Settings
} from 'lucide-react';
import { generateGoalsDocx } from '../../services/docxService';

const statusColors: Record<string, string> = {
  draft: 'bg-gray-100 text-gray-700',
  submitted: 'bg-yellow-100 text-yellow-700',
  initial_review: 'bg-blue-100 text-blue-700',
  approved: 'bg-green-100 text-green-700',
  final_review: 'bg-purple-100 text-purple-700',
  completed: 'bg-emerald-100 text-emerald-700',
};

const statusLabels: Record<string, string> = {
  draft: 'Draft',
  submitted: 'Awaiting Review',
  initial_review: 'Under Review',
  approved: 'Approved',
  final_review: 'Final Review',
  completed: 'Completed',
};

interface ExpandableSection {
  needsReview: boolean;
  reviewed: boolean;
  history: boolean;
}

const ManagerDashboard = () => {
  const { user, isDeveloper } = useAuth();
  const { isDemoMode } = useDemo();
  const navigate = useNavigate();
  const [teamMembers, setTeamMembers] = useState<TeamMemberWithLevel[]>([]);
  const [directReportsCount, setDirectReportsCount] = useState(0);
  const [allGoals, setAllGoals] = useState<GoalDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAllTeam, setShowAllTeam] = useState(false);
  const [expanded, setExpanded] = useState<ExpandableSection>({
    needsReview: true,
    reviewed: true,
    history: false,
  });
  const [fullscreen, setFullscreen] = useState<keyof ExpandableSection | null>(null);
  const [historySearch, setHistorySearch] = useState('');
  
  // Edit team member state
  const [editingMember, setEditingMember] = useState<TeamMemberWithLevel | null>(null);
  const [editTitle, setEditTitle] = useState('');
  const [savingTitle, setSavingTitle] = useState(false);
  
  // Self-review date modal state
  const [selfReviewGoal, setSelfReviewGoal] = useState<GoalDocument | null>(null);
  const [selfReviewDate, setSelfReviewDate] = useState<string>('');
  
  // Global self-review settings state (developer only)
  const [showGlobalSettings, setShowGlobalSettings] = useState(false);
  const [globalOpenDate, setGlobalOpenDate] = useState<string>('');
  const [globalQuarter, setGlobalQuarter] = useState<number>(1);
  const [globalYear, setGlobalYear] = useState<number>(new Date().getFullYear());
  const [currentGlobalSettings, setCurrentGlobalSettings] = useState<{
    openDate: Date | null;
    quarter: number | null;
    year: number | null;
  }>({ openDate: null, quarter: null, year: null });
  
  // Impersonation state
  const [allManagers, setAllManagers] = useState<RoleDocument[]>([]);
  const [viewingAsEmail, setViewingAsEmail] = useState<string | null>(null);
  const [currentUserCanImpersonate, setCurrentUserCanImpersonate] = useState(false);
  
  // Can this user impersonate? (developers always can, others need canImpersonate flag)
  const canImpersonate = isDeveloper || currentUserCanImpersonate;
  // Can take actions while impersonating? (only if canImpersonate flag is set OR is developer)
  const canActWhileImpersonating = isDeveloper || currentUserCanImpersonate;
  
  // Quarter filter state
  const { year: currentYear } = getCurrentQuarter();
  const [filterQuarter, setFilterQuarter] = useState<number | 'all'>('all');
  const [filterYear, setFilterYear] = useState<number | 'all'>(currentYear);
  const [showAllGoals, setShowAllGoals] = useState(false);

  // The email to use for fetching data (either impersonated or actual user)
  const activeEmail = viewingAsEmail || user?.email;

  // Fetch list of managers for impersonation and check current user's canImpersonate status
  useEffect(() => {
    const fetchManagers = async () => {
      if (!user?.email) return;
      
      const managers = await getAllManagers();
      
      // Check if current user has canImpersonate permission
      const currentUserRole = managers.find(m => m.email === user.email);
      if (currentUserRole?.canImpersonate) {
        setCurrentUserCanImpersonate(true);
      }
      
      // Only show manager list if user can impersonate
      if (isDeveloper || currentUserRole?.canImpersonate) {
        setAllManagers(managers);
      }
    };
    fetchManagers();
  }, [isDeveloper, user?.email]);

  // Fetch global self-review settings (developer and super_admin)
  useEffect(() => {
    const fetchGlobalSettings = async () => {
      if (!isDeveloper && user?.role !== 'super_admin') return;
      
      const settings = await getGlobalSelfReviewSettings();
      setCurrentGlobalSettings(settings);
      
      if (settings.openDate) {
        setGlobalOpenDate(settings.openDate.toISOString().split('T')[0]);
      }
      if (settings.quarter) {
        setGlobalQuarter(settings.quarter);
      }
      if (settings.year) {
        setGlobalYear(settings.year);
      }
    };
    fetchGlobalSettings();
  }, [isDeveloper, user?.role]);

  useEffect(() => {
    const fetchData = async () => {
      if (!activeEmail) return;

      setLoading(true);
      try {
        // Use demo data if demo mode is active
        if (isDemoMode) {
          const demoMembers: TeamMemberWithLevel[] = demoTeamAssignments.map((ta) => ({
            ...ta,
            level: 1,
            managerName: 'Demo Manager'
          }));
          setTeamMembers(demoMembers);
          setDirectReportsCount(demoMembers.length);
          setAllGoals(demoGoals);
          setLoading(false);
          return;
        }

        // Get all team members recursively
        const allMembers = await getTeamMembersRecursive(activeEmail);
        setTeamMembers(allMembers);
        console.log('Team members found:', allMembers.map(m => m.employeeEmail));

        // Count direct reports
        const directReports = allMembers.filter(m => m.level === 1);
        setDirectReportsCount(directReports.length);

        // Get ALL goals recursively (no quarter filter)
        const goals = await getTeamGoalsRecursive(activeEmail);
        console.log('Goals fetched:', goals.length, goals.map(g => `${g.userName} Q${g.quarter} ${g.year} - ${g.status}`));
        setAllGoals(goals);
      } catch (error) {
        console.error('Error fetching team data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [activeEmail, isDemoMode]);

  // Get direct report emails for filtering
  const directReportEmails = new Set(
    teamMembers.filter(m => m.level === 1).map(m => m.employeeEmail)
  );

  // Apply quarter/year filter to goals
  // Super admins/developers: default to direct reports only (can toggle to show all)
  // Regular managers: always show full org
  const filteredGoals = allGoals.filter(g => {
    const matchesQuarter = filterQuarter === 'all' || g.quarter === filterQuarter;
    const matchesYear = filterYear === 'all' || g.year === filterYear;
    const matchesScope = !isDeveloper || showAllGoals || directReportEmails.has(g.userEmail);
    return matchesQuarter && matchesYear && matchesScope;
  });

  // Categorize filtered goals
  const needsReviewGoals = filteredGoals.filter(g => 
    ['submitted', 'initial_review', 'final_review'].includes(g.status)
  );
  const inProgressGoals = filteredGoals.filter(g => g.status === 'approved');
  const completedGoals = filteredGoals.filter(g => g.status === 'completed');

  // Get available years from all goals
  const availableYears = [...new Set(allGoals.map(g => g.year))].sort((a, b) => b - a);

  const toggleExpanded = (section: keyof ExpandableSection) => {
    setExpanded(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const toggleFullscreen = (section: keyof ExpandableSection) => {
    setFullscreen(prev => prev === section ? null : section);
  };

  const filteredHistory = completedGoals.filter(goal => {
    const matchesSearch = historySearch === '' || 
      goal.userName.toLowerCase().includes(historySearch.toLowerCase());
    return matchesSearch;
  });

  // Group goals by quarter for display
  const groupByQuarter = (goals: GoalDocument[]) => {
    const grouped: Record<string, GoalDocument[]> = {};
    goals.forEach(goal => {
      const key = `Q${goal.quarter} ${goal.year}`;
      if (!grouped[key]) grouped[key] = [];
      grouped[key].push(goal);
    });
    // Sort by year desc, then quarter desc
    return Object.entries(grouped).sort((a, b) => {
      const [qA, yA] = a[0].replace('Q', '').split(' ');
      const [qB, yB] = b[0].replace('Q', '').split(' ');
      if (yB !== yA) return parseInt(yB) - parseInt(yA);
      return parseInt(qB) - parseInt(qA);
    });
  };

  const handleDownload = async (goal: GoalDocument) => {
    try {
      await generateGoalsDocx(goal);
    } catch (error) {
      console.error('Error generating DOCX:', error);
    }
  };

  const handleEditMember = (member: TeamMemberWithLevel) => {
    setEditingMember(member);
    setEditTitle(member.employeeTitle || '');
  };

  const handleSaveTitle = async () => {
    if (!editingMember) return;
    
    setSavingTitle(true);
    try {
      await updateTeamMemberTitle(editingMember.id!, editTitle);
      // Update local state
      setTeamMembers(prev => prev.map(m => 
        m.id === editingMember.id 
          ? { ...m, employeeTitle: editTitle }
          : m
      ));
      setEditingMember(null);
    } catch (error) {
      console.error('Error updating title:', error);
      alert('Failed to update title. Please try again.');
    } finally {
      setSavingTitle(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cps-primary"></div>
      </div>
    );
  }

  // Helper to check if goal owner is a direct report
  const isDirectReport = (goalUserEmail: string) => {
    const member = teamMembers.find(m => m.employeeEmail === goalUserEmail);
    return member?.level === 1;
  };

  // Helper to get the member's manager name for indirect reports
  const getMemberManager = (goalUserEmail: string) => {
    const member = teamMembers.find(m => m.employeeEmail === goalUserEmail);
    if (member && member.level > 1) {
      // Find who their direct manager is
      const manager = teamMembers.find(m => m.employeeEmail === member.managerEmail);
      return manager?.employeeName || member.managerEmail;
    }
    return null;
  };

  const handleToggleAllowEdit = async (goal: GoalDocument) => {
    if (!goal.id) return;
    try {
      const newValue = !goal.allowEdit;
      await toggleAllowEdit(goal.id, newValue);
      // Update local state
      setAllGoals(prev => prev.map(g => 
        g.id === goal.id ? { ...g, allowEdit: newValue } : g
      ));
    } catch (error) {
      console.error('Error toggling allow edit:', error);
    }
  };

  const handleOpenSelfReviewModal = (goal: GoalDocument) => {
    setSelfReviewGoal(goal);
    // Set default date to today if not already set
    if (goal.selfReviewOpenDate) {
      const date = goal.selfReviewOpenDate.toDate();
      setSelfReviewDate(date.toISOString().split('T')[0]);
    } else {
      setSelfReviewDate(new Date().toISOString().split('T')[0]);
    }
  };

  const handleSaveSelfReviewDate = async () => {
    if (!selfReviewGoal?.id) return;
    try {
      const date = selfReviewDate ? new Date(selfReviewDate) : null;
      await setSelfReviewOpenDate(selfReviewGoal.id, date);
      // Update local state
      setAllGoals(prev => prev.map(g => 
        g.id === selfReviewGoal.id ? { 
          ...g, 
          selfReviewOpenDate: date ? { toDate: () => date } as any : null 
        } : g
      ));
      setSelfReviewGoal(null);
    } catch (error) {
      console.error('Error setting self review date:', error);
    }
  };

  const handleClearSelfReviewDate = async () => {
    if (!selfReviewGoal?.id) return;
    try {
      await setSelfReviewOpenDate(selfReviewGoal.id, null);
      // Update local state
      setAllGoals(prev => prev.map(g => 
        g.id === selfReviewGoal.id ? { ...g, selfReviewOpenDate: null } : g
      ));
      setSelfReviewGoal(null);
    } catch (error) {
      console.error('Error clearing self review date:', error);
    }
  };

  const handleSaveGlobalSettings = async () => {
    try {
      const date = globalOpenDate ? new Date(globalOpenDate) : null;
      await setGlobalSelfReviewDate(date, globalQuarter, globalYear);
      setCurrentGlobalSettings({
        openDate: date,
        quarter: globalQuarter,
        year: globalYear,
      });
      setShowGlobalSettings(false);
    } catch (error) {
      console.error('Error saving global settings:', error);
    }
  };

  const handleClearGlobalSettings = async () => {
    try {
      await setGlobalSelfReviewDate(null, globalQuarter, globalYear);
      setCurrentGlobalSettings({ openDate: null, quarter: null, year: null });
      setGlobalOpenDate('');
      setShowGlobalSettings(false);
    } catch (error) {
      console.error('Error clearing global settings:', error);
    }
  };

  const renderGoalCard = (goal: GoalDocument, showActions: boolean = true) => {
    const isDirect = isDirectReport(goal.userEmail);
    const managerName = getMemberManager(goal.userEmail);
    
    // Can review if:
    // 1. Direct report AND user is the manager (not impersonating), OR
    // 2. Impersonating AND has canActWhileImpersonating permission AND goal belongs to impersonated manager
    const isOwnDirectReport = isDirect && goal.managerEmail === user?.email && !viewingAsEmail;
    const isImpersonatedDirectReport = viewingAsEmail && canActWhileImpersonating && goal.managerEmail === viewingAsEmail;
    const canReview = isOwnDirectReport || isImpersonatedDirectReport;
    
    // Developers can toggle allowEdit on any submitted goal, managers only on their direct reports
    const canToggleEdit = goal.status === 'submitted' && (isDeveloper || canReview);
    
    // Managers can set selfReviewOpenDate for their direct reports, developers can do it for any goal
    const canSetSelfReview = (goal.status === 'approved' || goal.status === 'submitted') && 
      (isDeveloper || canReview);
    
    // Check if self-review is currently open
    const selfReviewIsOpen = isSelfReviewOpenByDate(goal);
    
    return (
      <div 
        key={goal.id}
        className="p-4 border rounded-lg hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <p className="font-medium text-gray-900">{goal.userName}</p>
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusColors[goal.status]}`}>
                {statusLabels[goal.status]}
              </span>
              {goal.allowEdit && (
                <span className="px-2 py-0.5 rounded text-xs bg-blue-100 text-blue-700">
                  Edit Allowed
                </span>
              )}
              {selfReviewIsOpen && (
                <span className="px-2 py-0.5 rounded text-xs bg-purple-100 text-purple-700">
                  Self-Review Open
                </span>
              )}
              {goal.selfReviewOpenDate && !selfReviewIsOpen && (
                <span className="px-2 py-0.5 rounded text-xs bg-yellow-100 text-yellow-700">
                  Opens {goal.selfReviewOpenDate.toDate().toLocaleDateString()}
                </span>
              )}
              {!isDirect && (
                <span className="px-2 py-0.5 rounded text-xs bg-gray-100 text-gray-500">
                  via {managerName}
                </span>
              )}
            </div>
            <p className="text-sm text-gray-500">{goal.userTitle}</p>
            <p className="text-sm text-gray-400 mt-1">Q{goal.quarter} {goal.year}</p>
          </div>
          {showActions && (
            <div className="flex items-center gap-2">
              {canSetSelfReview && (
                <button
                  onClick={() => handleOpenSelfReviewModal(goal)}
                  className={`p-2 rounded-lg transition-colors ${
                    goal.selfReviewOpenDate 
                      ? 'text-purple-600 bg-purple-50 hover:bg-purple-100' 
                      : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
                  }`}
                  title="Set self-review open date"
                >
                  <Calendar className="w-4 h-4" />
                </button>
              )}
              {canToggleEdit && (
                <button
                  onClick={() => handleToggleAllowEdit(goal)}
                  className={`p-2 rounded-lg transition-colors ${
                    goal.allowEdit 
                      ? 'text-blue-600 bg-blue-50 hover:bg-blue-100' 
                      : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
                  }`}
                  title={goal.allowEdit ? 'Revoke edit permission' : 'Allow employee to edit'}
                >
                  <PenLine className="w-4 h-4" />
                </button>
              )}
              <button
                onClick={() => handleDownload(goal)}
                className="p-2 text-gray-400 hover:text-gray-600 rounded-lg transition-colors"
                title="Download DOCX"
              >
                <Download className="w-4 h-4" />
              </button>
              {goal.status === 'submitted' && canReview && (
                <button
                  onClick={() => navigate(`/goals/${goal.id}/review`)}
                  className="px-3 py-1.5 bg-cps-primary text-white text-sm rounded-lg hover:bg-cps-light transition-colors"
                >
                  Review
                </button>
              )}
              {goal.status === 'final_review' && canReview && (
                <button
                  onClick={() => navigate(`/goals/${goal.id}/final-review`)}
                  className="px-3 py-1.5 bg-purple-600 text-white text-sm rounded-lg hover:bg-purple-700 transition-colors"
                >
                  Final Review
                </button>
              )}
              {/* View button for all - including indirect reports */}
              <button
                onClick={() => navigate(`/goals/${goal.id}`)}
                className="px-3 py-1.5 border border-gray-300 text-gray-700 text-sm rounded-lg hover:bg-gray-50 transition-colors"
              >
                View
              </button>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderSection = (
    section: keyof ExpandableSection,
    title: string,
    icon: React.ReactNode,
    count: number,
    content: React.ReactNode
  ) => {
    const isFullscreen = fullscreen === section;
    
    if (fullscreen && fullscreen !== section) {
      return null;
    }

    return (
      <div className={`bg-white rounded-xl shadow-sm overflow-hidden ${isFullscreen ? 'fixed inset-4 z-50' : ''}`}>
        <div className="p-4 border-b flex items-center justify-between">
          <button
            onClick={() => toggleExpanded(section)}
            className="flex items-center gap-2 flex-1"
          >
            {icon}
            <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
            <span className="text-sm text-gray-500">({count})</span>
            {expanded[section] ? (
              <ChevronUp className="w-5 h-5 text-gray-400 ml-auto" />
            ) : (
              <ChevronDown className="w-5 h-5 text-gray-400 ml-auto" />
            )}
          </button>
          <button
            onClick={() => toggleFullscreen(section)}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-lg transition-colors ml-2"
            title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
          >
            {isFullscreen ? (
              <Minimize2 className="w-4 h-4" />
            ) : (
              <Maximize2 className="w-4 h-4" />
            )}
          </button>
        </div>
        
        {expanded[section] && (
          <div className={`${isFullscreen ? 'overflow-auto' : ''}`} style={isFullscreen ? { maxHeight: 'calc(100vh - 120px)' } : {}}>
            {content}
          </div>
        )}
      </div>
    );
  };

  const handleToggleCanImpersonate = async (manager: RoleDocument) => {
    try {
      const newValue = !manager.canImpersonate;
      await toggleCanImpersonate(manager.email, newValue);
      // Update local state
      setAllManagers(prev => prev.map(m => 
        m.email === manager.email ? { ...m, canImpersonate: newValue } : m
      ));
    } catch (error) {
      console.error('Error toggling canImpersonate:', error);
    }
  };

  return (
    <div className="space-y-6">
      {/* Impersonation Banner - visible to developers and users with canImpersonate */}
      {canImpersonate && (
        <div className="bg-purple-50 border border-purple-200 rounded-xl p-4">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-2 text-purple-700">
              <Eye className="w-5 h-5" />
              <span className="font-medium">{isDeveloper ? 'Developer View' : 'Admin View'}</span>
            </div>
            <select
              value={viewingAsEmail || ''}
              onChange={(e) => setViewingAsEmail(e.target.value || null)}
              className="flex-1 min-w-[200px] px-3 py-1.5 border border-purple-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm bg-white"
            >
              <option value="">View as myself ({user?.email})</option>
              {allManagers
                .filter(m => m.email !== user?.email)
                .sort((a, b) => (a.displayName || a.email).localeCompare(b.displayName || b.email))
                .map(manager => (
                  <option key={manager.email} value={manager.email}>
                    {manager.displayName || manager.email}
                    {manager.canImpersonate ? ' ⭐' : ''}
                  </option>
                ))
              }
            </select>
            {viewingAsEmail && (
              <button
                onClick={() => setViewingAsEmail(null)}
                className="px-3 py-1.5 text-purple-700 hover:bg-purple-100 rounded-lg text-sm font-medium transition-colors"
              >
                Reset
              </button>
            )}
            {(isDeveloper || user?.role === 'super_admin') && (
              <button
                onClick={() => setShowGlobalSettings(true)}
                className="px-3 py-1.5 bg-purple-600 text-white hover:bg-purple-700 rounded-lg text-sm font-medium transition-colors flex items-center gap-2"
              >
                <Settings className="w-4 h-4" />
                Global Settings
              </button>
            )}
          </div>
          {viewingAsEmail && (
            <p className="text-xs text-purple-600 mt-2">
              Viewing as: <span className="font-medium">{viewingAsEmail}</span>
              {canActWhileImpersonating ? ' (can take actions)' : ' (read-only)'}
            </p>
          )}
          
          {/* Show current global self-review settings */}
          {(isDeveloper || user?.role === 'super_admin') && currentGlobalSettings.openDate && (
            <div className="mt-3 pt-3 border-t border-purple-200 flex items-center gap-2 text-sm text-purple-700">
              <Calendar className="w-4 h-4" />
              <span>
                Global self-review for Q{currentGlobalSettings.quarter} {currentGlobalSettings.year}: 
                <strong className="ml-1">
                  {currentGlobalSettings.openDate <= new Date() ? 'Open now' : `Opens ${currentGlobalSettings.openDate.toLocaleDateString()}`}
                </strong>
              </span>
            </div>
          )}
          
          {/* Developer-only: Toggle canImpersonate for other admins */}
          {isDeveloper && viewingAsEmail && (
            <div className="mt-3 pt-3 border-t border-purple-200">
              {(() => {
                const selectedManager = allManagers.find(m => m.email === viewingAsEmail);
                if (!selectedManager || selectedManager.role === 'developer') return null;
                return (
                  <label className="flex items-center gap-2 cursor-pointer text-sm">
                    <input
                      type="checkbox"
                      checked={selectedManager.canImpersonate || false}
                      onChange={() => handleToggleCanImpersonate(selectedManager)}
                      className="rounded border-purple-300 text-purple-600 focus:ring-purple-500"
                    />
                    <span className="text-purple-700">
                      Allow <strong>{selectedManager.displayName || selectedManager.email}</strong> to impersonate managers
                    </span>
                  </label>
                );
              })()}
            </div>
          )}
        </div>
      )}

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {viewingAsEmail ? `${allManagers.find(m => m.email === viewingAsEmail)?.displayName || viewingAsEmail}'s Team` : 'Team Goals'}
          </h1>
          <p className="text-gray-500">
            {directReportsCount} direct report{directReportsCount !== 1 ? 's' : ''}
            {teamMembers.length > directReportsCount && (
              <span className="text-gray-400"> • {teamMembers.length} total in org</span>
            )}
          </p>
        </div>
        
        {/* Quarter Filter */}
        <div className="flex items-center gap-2 bg-white rounded-lg shadow-sm p-2">
          <Filter className="w-4 h-4 text-gray-400" />
          <select
            value={filterQuarter}
            onChange={(e) => setFilterQuarter(e.target.value === 'all' ? 'all' : Number(e.target.value))}
            className="px-2 py-1 border rounded focus:ring-2 focus:ring-cps-primary focus:border-cps-primary text-sm"
          >
            <option value="all">All Quarters</option>
            <option value={1}>Q1</option>
            <option value={2}>Q2</option>
            <option value={3}>Q3</option>
            <option value={4}>Q4</option>
          </select>
          <select
            value={filterYear}
            onChange={(e) => setFilterYear(e.target.value === 'all' ? 'all' : Number(e.target.value))}
            className="px-2 py-1 border rounded focus:ring-2 focus:ring-cps-primary focus:border-cps-primary text-sm"
          >
            <option value="all">All Years</option>
            {availableYears.length > 0 ? (
              availableYears.map(y => (
                <option key={y} value={y}>{y}</option>
              ))
            ) : (
              <>
                <option value={currentYear}>{currentYear}</option>
                <option value={currentYear - 1}>{currentYear - 1}</option>
              </>
            )}
          </select>
          {(filterQuarter !== 'all' || filterYear !== 'all') && (
            <button
              onClick={() => {
                setFilterQuarter('all');
                setFilterYear('all');
              }}
              className="text-xs text-cps-primary hover:text-cps-dark"
            >
              Clear
            </button>
          )}
          {teamMembers.length > directReportsCount && isDeveloper && (
            <>
              <div className="w-px h-6 bg-gray-200 mx-1" />
              <label className="flex items-center gap-2 cursor-pointer text-sm">
                <input
                  type="checkbox"
                  checked={showAllGoals}
                  onChange={(e) => setShowAllGoals(e.target.checked)}
                  className="rounded border-gray-300 text-cps-primary focus:ring-cps-primary"
                />
                <span className="text-gray-600">Show full org</span>
              </label>
            </>
          )}
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Users className="w-5 h-5 text-cps-primary" />
            <h2 className="text-lg font-semibold text-gray-900">Team Members</h2>
          </div>
          {teamMembers.length > directReportsCount && (
            <button
              onClick={() => setShowAllTeam(!showAllTeam)}
              className="text-sm text-cps-primary hover:text-cps-dark flex items-center gap-1"
            >
              {showAllTeam ? 'Show Direct Reports Only' : 'Show Full Org'}
              <ChevronRight className={`w-4 h-4 transition-transform ${showAllTeam ? 'rotate-90' : ''}`} />
            </button>
          )}
        </div>
        
        <div className="space-y-2">
          {(showAllTeam ? teamMembers : teamMembers.filter(m => m.level === 1)).map(member => (
            <div 
              key={member.id} 
              className="p-3 bg-gray-50 rounded-lg flex items-center gap-3"
              style={{ marginLeft: showAllTeam ? `${(member.level - 1) * 24}px` : 0 }}
            >
              {showAllTeam && member.level > 1 && (
                <span className="text-gray-300">└</span>
              )}
              <div className="flex-1">
                <p className="font-medium text-gray-900 text-sm">{member.employeeName}</p>
                <p className="text-xs text-gray-500">{member.employeeTitle || 'No title set'}</p>
              </div>
              {showAllTeam && member.level > 1 && (
                <span className="text-xs text-gray-400 bg-gray-200 px-2 py-0.5 rounded">
                  Level {member.level}
                </span>
              )}
              {/* Only allow editing direct reports when not impersonating, or when impersonating with permission */}
              {member.level === 1 && (!viewingAsEmail || canActWhileImpersonating) && (
                <button
                  onClick={() => handleEditMember(member)}
                  className="p-1.5 text-gray-400 hover:text-cps-primary hover:bg-white rounded transition-colors"
                  title="Edit title"
                >
                  <Pencil className="w-4 h-4" />
                </button>
              )}
            </div>
          ))}
          {teamMembers.length === 0 && (
            <p className="text-gray-500 text-center py-4">No team members assigned</p>
          )}
        </div>
      </div>

      {/* Edit Title Modal */}
      {editingMember && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Edit Title</h3>
              <button
                onClick={() => setEditingMember(null)}
                className="p-1 text-gray-400 hover:text-gray-600 rounded"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="mb-4">
              <p className="text-sm text-gray-600 mb-1">Employee</p>
              <p className="font-medium text-gray-900">{editingMember.employeeName}</p>
              <p className="text-xs text-gray-500">{editingMember.employeeEmail}</p>
            </div>
            
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Job Title
              </label>
              <input
                type="text"
                value={editTitle}
                onChange={(e) => setEditTitle(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-cps-primary focus:border-cps-primary"
                placeholder="Enter job title..."
                autoFocus
              />
            </div>
            
            <div className="flex gap-3">
              <button
                onClick={() => setEditingMember(null)}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveTitle}
                disabled={savingTitle}
                className="flex-1 px-4 py-2 bg-cps-primary text-white rounded-lg hover:bg-cps-light transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {savingTitle ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    Save
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Self-Review Date Modal */}
      {selfReviewGoal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Set Self-Review Open Date</h3>
              <button
                onClick={() => setSelfReviewGoal(null)}
                className="p-1 text-gray-400 hover:text-gray-600 rounded"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="mb-4">
              <p className="text-sm text-gray-600 mb-1">Employee</p>
              <p className="font-medium text-gray-900">{selfReviewGoal.userName}</p>
              <p className="text-xs text-gray-500">Q{selfReviewGoal.quarter} {selfReviewGoal.year}</p>
            </div>
            
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Open Date
              </label>
              <input
                type="date"
                value={selfReviewDate}
                onChange={(e) => setSelfReviewDate(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
              />
              <p className="text-xs text-gray-500 mt-1">
                Self-review will be available starting on this date
              </p>
            </div>
            
            <div className="flex gap-3">
              {selfReviewGoal.selfReviewOpenDate && (
                <button
                  onClick={handleClearSelfReviewDate}
                  className="px-4 py-2 border border-red-300 text-red-700 rounded-lg hover:bg-red-50 transition-colors"
                >
                  Clear
                </button>
              )}
              <button
                onClick={() => setSelfReviewGoal(null)}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveSelfReviewDate}
                className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center justify-center gap-2"
              >
                <Calendar className="w-4 h-4" />
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Global Self-Review Settings Modal (Developer Only) */}
      {showGlobalSettings && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Global Self-Review Settings</h3>
              <button
                onClick={() => setShowGlobalSettings(false)}
                className="p-1 text-gray-400 hover:text-gray-600 rounded"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <p className="text-sm text-gray-600 mb-4">
              Open self-review for <strong>all employees</strong> for a specific quarter, regardless of individual goal status.
            </p>
            
            <div className="space-y-4 mb-6">
              <div className="flex gap-4">
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Quarter
                  </label>
                  <select
                    value={globalQuarter}
                    onChange={(e) => setGlobalQuarter(Number(e.target.value))}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  >
                    <option value={1}>Q1</option>
                    <option value={2}>Q2</option>
                    <option value={3}>Q3</option>
                    <option value={4}>Q4</option>
                  </select>
                </div>
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Year
                  </label>
                  <select
                    value={globalYear}
                    onChange={(e) => setGlobalYear(Number(e.target.value))}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  >
                    {[currentYear - 1, currentYear, currentYear + 1].map(y => (
                      <option key={y} value={y}>{y}</option>
                    ))}
                  </select>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Open Date
                </label>
                <input
                  type="date"
                  value={globalOpenDate}
                  onChange={(e) => setGlobalOpenDate(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Self-review for Q{globalQuarter} {globalYear} will open on this date for all employees
                </p>
              </div>
            </div>
            
            <div className="flex gap-3">
              {currentGlobalSettings.openDate && (
                <button
                  onClick={handleClearGlobalSettings}
                  className="px-4 py-2 border border-red-300 text-red-700 rounded-lg hover:bg-red-50 transition-colors"
                >
                  Clear
                </button>
              )}
              <button
                onClick={() => setShowGlobalSettings(false)}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveGlobalSettings}
                disabled={!globalOpenDate}
                className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                <Save className="w-4 h-4" />
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {fullscreen && (
        <div className="fixed inset-0 bg-black/50 z-40" onClick={() => setFullscreen(null)} />
      )}

      {renderSection(
        'needsReview',
        'Needs Review',
        <AlertCircle className="w-5 h-5 text-yellow-500" />,
        needsReviewGoals.length,
        <div className="p-4 space-y-4">
          {needsReviewGoals.length > 0 ? (
            groupByQuarter(needsReviewGoals).map(([quarterLabel, goals]) => (
              <div key={quarterLabel}>
                <h3 className="text-sm font-semibold text-gray-500 mb-2 flex items-center gap-2">
                  <span className="bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded">{quarterLabel}</span>
                  <span className="text-gray-400">({goals.length})</span>
                </h3>
                <div className="space-y-3">
                  {goals.map(goal => renderGoalCard(goal))}
                </div>
              </div>
            ))
          ) : (
            <p className="text-gray-500 text-center py-8">No goals need review</p>
          )}
        </div>
      )}

      {renderSection(
        'reviewed',
        'In Progress',
        <CheckCircle2 className="w-5 h-5 text-green-500" />,
        inProgressGoals.length,
        <div className="p-4 space-y-4">
          {inProgressGoals.length > 0 ? (
            groupByQuarter(inProgressGoals).map(([quarterLabel, goals]) => (
              <div key={quarterLabel}>
                <h3 className="text-sm font-semibold text-gray-500 mb-2 flex items-center gap-2">
                  <span className="bg-green-100 text-green-700 px-2 py-0.5 rounded">{quarterLabel}</span>
                  <span className="text-gray-400">({goals.length})</span>
                </h3>
                <div className="space-y-3">
                  {goals.map(goal => renderGoalCard(goal))}
                </div>
              </div>
            ))
          ) : (
            <p className="text-gray-500 text-center py-8">No goals in progress</p>
          )}
        </div>
      )}

      {renderSection(
        'history',
        'Completed',
        <History className="w-5 h-5 text-gray-400" />,
        completedGoals.length,
        <div>
          <div className="p-4 border-b bg-gray-50">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search by name..."
                value={historySearch}
                onChange={(e) => setHistorySearch(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-cps-primary focus:border-cps-primary"
              />
            </div>
          </div>
          
          <div className="p-4 space-y-4">
            {filteredHistory.length > 0 ? (
              groupByQuarter(filteredHistory).map(([quarterLabel, goals]) => (
                <div key={quarterLabel}>
                  <h3 className="text-sm font-semibold text-gray-500 mb-2 flex items-center gap-2">
                    <span className="bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded">{quarterLabel}</span>
                    <span className="text-gray-400">({goals.length})</span>
                  </h3>
                  <div className="space-y-3">
                    {goals.map(goal => renderGoalCard(goal))}
                  </div>
                </div>
              ))
            ) : (
              <p className="text-gray-500 text-center py-8">No completed goals found</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ManagerDashboard;